import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Interview, InterviewMeetingType, ProposeInterviewPayload } from '../models/interview.model';
import { InterviewService } from '../services/interview.service';

dayjs.extend(utc);

interface Props {
  open: boolean;
  jobApplicationId: string;
  interview?: Interview | null;
  onClose: () => void;
  onSaved: () => void;
}

const ProposeInterviewModal: React.FC<Props> = ({
  open,
  jobApplicationId,
  interview,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isReschedule = Boolean(interview);

  const initialValues = useMemo(
    () => ({
      meetingType: interview?.meetingType || 'VIDEO',
      durationMinutes: interview?.durationMinutes || 60,
      locationOrLink: interview?.locationOrLink || '',
      timezone:
        interview?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Costa_Rica',
      slots: [{ startsAt: null as Dayjs | null }, { startsAt: null as Dayjs | null }],
    }),
    [interview]
  );

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const durationMinutes = values.durationMinutes as number;
      const slots = (values.slots as { startsAt: Dayjs | null }[])
        .filter((s) => s?.startsAt)
        .map((s) => {
          const start = s.startsAt!.utc();
          const end = start.add(durationMinutes, 'minute');
          return {
            startsAt: start.format('YYYY-MM-DDTHH:mm:ss'),
            endsAt: end.format('YYYY-MM-DDTHH:mm:ss'),
          };
        });
      if (slots.length < 1 || slots.length > 3) {
        message.error('Add between 1 and 3 time slots');
        return;
      }
      const payload: ProposeInterviewPayload = {
        meetingType: values.meetingType as InterviewMeetingType,
        durationMinutes,
        locationOrLink: values.locationOrLink || undefined,
        timezone: values.timezone,
        slots,
      };
      setSubmitting(true);
      if (isReschedule && interview) {
        await InterviewService.reschedule(interview.id, payload);
        message.success('Interview rescheduled');
      } else {
        await InterviewService.propose(jobApplicationId, payload);
        message.success('Interview proposed');
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error('Could not save interview proposal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isReschedule ? 'Reschedule interview' : 'Propose interview'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={submitting}
      okText={isReschedule ? 'Reschedule' : 'Send proposal'}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item name="meetingType" label="Type" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'VIDEO', label: 'Video' },
              { value: 'CALL', label: 'Call' },
              { value: 'IN_PERSON', label: 'In person' },
            ]}
          />
        </Form.Item>
        <Form.Item name="durationMinutes" label="Duration (minutes)" rules={[{ required: true }]}>
          <InputNumber min={15} max={480} step={15} className="w-full" />
        </Form.Item>
        <Form.Item name="locationOrLink" label="Location or meeting link">
          <Input placeholder="https://meet.google.com/..." />
        </Form.Item>
        <Form.Item name="timezone" label="Timezone" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.List name="slots">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Space key={field.key} align="baseline" className="mb-2 flex w-full">
                  <Form.Item
                    {...field}
                    name={[field.name, 'startsAt']}
                    label={index === 0 ? 'Proposed start times (stored as UTC)' : ''}
                    rules={[{ required: index === 0, message: 'Add at least one slot' }]}
                  >
                    <DatePicker showTime format="YYYY-MM-DD HH:mm" className="min-w-[280px]" />
                  </Form.Item>
                  {fields.length > 1 && (
                    <Button type="link" onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  )}
                </Space>
              ))}
              {fields.length < 3 && (
                <Button type="dashed" onClick={() => add({ startsAt: null })} block>
                  Add another slot
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ProposeInterviewModal;
